// Hodnocení výživy (1. 11. 2026, zadání Kristiny, schválené mockupy 6A v4, 6B v2 a 6E).
// Sales (Miluška) obvolává klientky, které měly v minulém měsíci nutriční konzultaci,
// a zapisuje krátký dotazník spokojenosti. Nahoře postup Hotovo X ze Y, pod tím
// Na řadě (Zavolat, Zkusit znovu s poznámkou) a Hotovo s hvězdičkami, dole report měsíce.
// Obrazovka hovoru je okno: Zavolat (tel:), co říct na začátku, výsledek hovoru
// (Dovoláno / Nedovoláno, 1. pokus / Nedovoláno, uzavřeno / Nechce hodnotit), otázky 1 až 7
// s poznámkami, dřívější hodnocení klientky a e-mail s odkazem na Google (náhled před odesláním).
// Data: /api/hodnoceni-vyzivy (role owner, CSM, sales; server kontroluje roli).
// O klientce (mockup 8A a 8B, 1. 11. 2026): tlačítko u každé klientky (Na řadě i Hotovo)
// otevře krátký přehled pro hovor, stejný obsah je rozbalovací i v obrazovce hovoru.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Soup, Phone, PhoneCall, Star, ChevronRight, ChevronDown, Send, Mic, X, NotebookPen, CheckCircle2, Mail, BarChart3, History, Lock, PhoneOff } from "lucide-react";
import { Stranka, Modal, HlavickaOkna, ListovaniMesice, SkeletonRows, ErrorNote, EmptyState, Hlaska, Zapinatko, Stitek, Segment } from "../../components/ui.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { Pil } from "../../components/sales/salesUi.jsx";
import { telefonHezky } from "../../components/sales/salesApi.js";
import { toast } from "../../components/Toast.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { OKlientceTlacitko, OKlientceOkno, OKlientceRozbalovaci } from "../../components/sales/OKlientce.jsx";
import {
  OTAZKY_VYZIVA, nazevMesice, mesicKratce, datumKratce, krestni, domaciJmeno, inicialy, hvGet, hvUloz, hvGoogle, hvPost, oznamPocet,
  STAV_POPIS, datumZCasuSRokem, popisOdpovedi,
} from "../../lib/hodnoceniVyzivy.js";

function pragueDnes() { return new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" })); }
function mesicKlic(offset) {
  const d = pragueDnes();
  const m = new Date(Date.UTC(d.getFullYear(), d.getMonth() + offset, 1));
  return `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, "0")}`;
}
function posunMesic(k, n) {
  const [y, m] = k.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
const platnyMesic = (m) => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(m || ""));
/** Obvolat do 10. následujícího měsíce. */
function termin(mesic) { const n = posunMesic(mesic, 1); return `10. ${Number(n.split("-")[1])}.`; }
/** Martina -> Martinou (7. pád ženských jmen na -a), jinak beze změny. */
function sKym(jmeno) { const k = krestni(jmeno); return /a$/i.test(k) ? `${k.slice(0, -1)}ou` : k; }
function zbyvaji(n) { return n === 1 ? "zbývá 1" : n >= 2 && n <= 4 ? `zbývají ${n}` : `zbývá ${n}`; }
function hvezdTvar(n) { return n === 1 ? "hvězda" : n >= 2 && n <= 4 ? "hvězdy" : "hvězd"; }
const cz = (n) => (n == null ? "?" : String(n).replace(".", ","));

/** Hvězdičky jako pilulka (Hotovo, karta klientky). 4 a 5 zeleně, 1 až 3 růžově. */
export function HvezdyPil({ n }) {
  const v = Number(n) || 0;
  const dobre = v >= 4;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[13px] font-bold ${dobre ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
      aria-label={`${v} z 5 hvězd`}>
      <span aria-hidden className="tracking-tight">{"★".repeat(v)}</span>
      <span className="tabular-nums">{v}</span>
    </span>
  );
}

function Avatar({ jmeno, svetly = false }) {
  return (
    <span aria-hidden className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${svetly ? "bg-brand-400" : "bg-brand"}`}>
      {inicialy(jmeno)}
    </span>
  );
}

function KartaNaRade({ p, onOtevrit, onOKlientce }) {
  const znovu = p.stav === "zkusit_znovu";
  const tlacitko = znovu ? (
    <button type="button" onClick={() => onOtevrit(p)}
      className="btn inline-flex min-h-[48px] flex-[3] items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-amber-300 bg-amber-50 px-4 text-[15px] font-bold text-amber-800 transition active:scale-[0.98] sm:flex-none sm:hover:bg-amber-100">
      <PhoneCall className="h-5 w-5" />Zkusit znovu
    </button>
  ) : (
    <button type="button" onClick={() => onOtevrit(p)} className="btn-primary min-h-[48px] flex-[3] justify-center whitespace-nowrap px-5 sm:flex-none">
      <Phone className="h-5 w-5" />Zavolat
    </button>
  );
  return (
    <div className={`rounded-2xl border bg-white p-3 shadow-sm sm:p-4 ${znovu ? "border-amber-200" : "border-slate-200"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Avatar jmeno={p.klient_name} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-bold text-slate-900">{p.klient_name}</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <Pil casti={`Konzultace ${datumKratce(p.datum)}`} />
              {p.nutricni && <Pil ton="znacka" casti={krestni(p.nutricni)} />}
              {znovu && p.zaznam?.poznamka_znovu && <span className="inline-flex max-w-full items-center whitespace-normal break-words rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">{p.zaznam.poznamka_znovu}</span>}
            </div>
          </div>
        </div>
        {/* Mobil: O klientce vlevo užší, Zavolat vpravo širší (mockup 8A). */}
        <div className="flex w-full gap-2 sm:w-auto">
          <OKlientceTlacitko onClick={() => onOKlientce(p)} className="flex-[2] sm:flex-none" />
          {tlacitko}
        </div>
      </div>
    </div>
  );
}

function RadekHotovo({ p, onOtevrit, onOKlientce }) {
  // Řádek otevře hovor, O klientce je samostatné tlačítko vedle (tlačítko v tlačítku nejde).
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:gap-3 sm:p-4">
    <button type="button" onClick={() => onOtevrit(p)}
      className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left transition sm:hover:bg-brand-50/50">
      <Avatar jmeno={p.klient_name} svetly />
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-semibold text-slate-800">{p.klient_name}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Pil casti={`Konzultace ${datumKratce(p.datum)}`} />
        </div>
      </div>
      {p.stav === "nechce" ? <Pil casti="Nechce hodnotit" />
        : p.stav === "nedovolano" ? <Pil ton="pozor" casti="Nedovoláno" />
          : <HvezdyPil n={p.zaznam?.hvezdy} />}
    </button>
    <OKlientceTlacitko onClick={() => onOKlientce(p)} className="w-full sm:w-auto" />
    </div>
  );
}

function Nadpis({ children }) {
  return <div className="px-1 pt-1 text-[12px] font-bold uppercase tracking-wider text-slate-500">{children}</div>;
}

export default function HodnoceniVyzivy() {
  const [params, setParams] = useSearchParams();
  const reportParam = platnyMesic(params.get("report")) && params.get("report") <= mesicKlic(-1) ? params.get("report") : null;
  const [mesic, setMesic] = useState(() => reportParam || mesicKlic(-1));
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [nacita, setNacita] = useState(true);
  const [otevrena, setOtevrena] = useState(null);
  const [oKlientce, setOKlientce] = useState(null);
  const [reportOtevren, setReportOtevren] = useState(!!reportParam);
  const [uzavira, setUzavira] = useState(false);

  const nacti = useCallback(async () => {
    setNacita(true);
    try {
      const d = await hvGet("seznam", { mesic });
      setData(d); setChyba("");
      if (mesic === mesicKlic(-1)) oznamPocet(d.zbyva);
      return d;
    } catch (e) { setChyba(e.message); return null; }
    finally { setNacita(false); }
  }, [mesic]);
  useEffect(() => { nacti(); }, [nacti]);

  const celkem = data?.celkem || 0;
  const hotovo = data?.hotovoPocet || 0;
  const procent = celkem ? Math.round((hotovo / celkem) * 100) : 0;
  const aktualni = mesic === mesicKlic(-1);
  const report = data && data.mesic === mesic ? data.report : null;

  const poUlozeni = async (puvodni) => {
    const d = await nacti();
    const dalsi = (d?.naRade || []).find((p) => p.klient_id !== puvodni.klient_id);
    setOtevrena(dalsi || null);
    if (!dalsi && d && d.zbyva === 0) toast("Všechny klientky jsou obvolané, děkujeme!", "success");
  };

  const zavritReport = () => {
    setReportOtevren(false);
    if (params.get("report")) { const n = new URLSearchParams(params); n.delete("report"); setParams(n, { replace: true }); }
  };

  const uzavritMesic = async () => {
    if (uzavira) return;
    const zbyva = data?.zbyva || 0;
    if (zbyva > 0) {
      const ok = await confirmDialog({
        title: "Uzavřít měsíc a vytvořit report?",
        description: `${zbyva === 1 ? "1 klientka bez výsledku hovoru se zapíše" : `${zbyva} klientek bez výsledku hovoru se zapíše`} jako nedovolaná a už se volat nebude. Pak vznikne report a jeho shrnutí se přidá do nejbližšího měsíčního meetingu.`,
        confirmLabel: "Uzavřít a vytvořit report",
        variant: "primary",
      });
      if (!ok) return;
    }
    setUzavira(true);
    try {
      await hvPost("report", { mesic, uzavrit: true });
      await nacti();
      setReportOtevren(true);
      toast("Report je hotový a shrnutí je v měsíčním meetingu.", "success");
    } catch (e) { toast(e.message, "error"); }
    finally { setUzavira(false); }
  };

  return (
    <Stranka sirka="uzka">
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-10 sm:w-10">
            <Soup className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-serif text-xl font-bold leading-tight sm:text-2xl">Hodnocení výživy</h1>
            <div className="truncate text-[13px] text-white/75">{nazevMesice(mesic)}{aktualni ? `, obvolat do ${termin(mesic)}` : ""}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-[13px] text-white/85">
          <span>Hotovo {hotovo} ze {celkem}</span>
          <span>{zbyvaji(data?.zbyva || 0)}</span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/20" role="progressbar" aria-valuenow={procent} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-brand-gold transition-all" style={{ width: `${procent}%` }} />
        </div>
      </div>

      <ListovaniMesice
        popis={nazevMesice(mesic)}
        onZpet={() => setMesic((m) => posunMesic(m, -1))}
        onVpred={() => setMesic((m) => posunMesic(m, 1))}
        vpredVypnuto={mesic >= mesicKlic(-1)}
        naDnesek={aktualni ? undefined : () => setMesic(mesicKlic(-1))}
        popisDneska="Na minulý měsíc"
      />

      {chyba && <ErrorNote onRetry={nacti}>{chyba}</ErrorNote>}
      {nacita && !data && <SkeletonRows rows={5} />}

      {data && celkem === 0 && (
        <EmptyState icon={Soup} title="Žádná nutriční konzultace" hint={`V měsíci ${nazevMesice(mesic).toLowerCase()} neměla žádná klientka k obvolání nutriční konzultaci.`} />
      )}

      {data && data.naRade.length > 0 && (
        <div className="space-y-2.5">
          <Nadpis>Na řadě</Nadpis>
          {data.naRade.map((p) => <KartaNaRade key={p.klient_id} p={p} onOtevrit={setOtevrena} onOKlientce={setOKlientce} />)}
        </div>
      )}

      {data && data.hotovo.length > 0 && (
        <div className="space-y-2.5">
          <Nadpis>Hotovo</Nadpis>
          {data.hotovo.map((p) => <RadekHotovo key={p.klient_id} p={p} onOtevrit={setOtevrena} onOKlientce={setOKlientce} />)}
        </div>
      )}

      {data && celkem > 0 && (report || (data.lzeUzavrit && aktualni)) && (
        <div className="space-y-2.5">
          <Nadpis>Report do měsíčního meetingu</Nadpis>
          {report ? (
            <div className="rounded-2xl border border-brand-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-deep"><BarChart3 className="h-5 w-5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-bold text-slate-900">Report za {nazevMesice(mesic).toLowerCase()}</div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {report.prumer != null && <Pil ton="znacka" casti={`Průměr ${cz(report.prumer)} ★`} />}
                      <Pil casti={`Dovoláno ${report.dovolano} z ${report.konzultaci}`} />
                      {(report.nespokojene || []).length > 0 && <Pil ton="pozor" casti={`Nespokojené ${report.nespokojene.length}`} />}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => setReportOtevren(true)} className="btn-primary min-h-[48px] justify-center px-5">
                  <BarChart3 className="h-5 w-5" />Zobrazit report
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="flex-1 text-[14px] leading-snug text-slate-600">
                  {data.zbyva > 0
                    ? "Report vznikne sám, až budou mít všechny klientky výsledek hovoru. Když už volat nechceš, uzavři měsíc a nedokončené se zapíšou jako nedovolané."
                    : "Všechny klientky jsou uzavřené, report se ale nevytvořil. Vytvoř ho tlačítkem."}
                </p>
                <button type="button" onClick={uzavritMesic} disabled={uzavira} className="btn-secondary min-h-[48px] justify-center px-5">
                  <BarChart3 className="h-5 w-5" />{uzavira ? "Vytvářím…" : data.zbyva > 0 ? "Uzavřít a vytvořit report" : "Vytvořit report"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {otevrena && (
        <HovorOkno
          key={`${otevrena.klient_id}-${mesic}`}
          p={otevrena}
          mesic={mesic}
          zbyvaDalsi={(data?.naRade || []).some((x) => x.klient_id !== otevrena.klient_id)}
          onClose={() => setOtevrena(null)}
          onUlozeno={poUlozeni}
          onObnov={nacti}
        />
      )}
      {oKlientce && !otevrena && (
        <OKlientceOkno key={oKlientce.klient_id} klientId={oKlientce.klient_id} jmeno={oKlientce.klient_name}
          onZavolat={() => { setOtevrena(oKlientce); setOKlientce(null); }} onClose={() => setOKlientce(null)} />
      )}
      {reportOtevren && report && <ReportOkno r={report} mesic={mesic} onClose={zavritReport} />}
    </Stranka>
  );
}

/* --- Report měsíce ---------------------------------------------------------- */

function Cislo({ popis, hodnota, ton = "" }) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${ton === "pozor" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[12px] font-semibold text-slate-500">{popis}</div>
      <div className="text-xl font-bold tabular-nums text-slate-900">{hodnota}</div>
    </div>
  );
}

const ROZDELENI = [
  { k: "informace", nazev: "Množství informací", moznosti: [["akorat", "Akorát"], ["chybelo", "Něco chybělo"], ["moc", "Moc"]] },
  { k: "mp_zajem", nazev: "Masterplan, zájem", moznosti: [["ano", "Ano"], ["ne", "Ne"]] },
  { k: "mp_dodrzuje", nazev: "Masterplan, dodržování", moznosti: [["ano", "Ano"], ["castecne", "Částečně"], ["ne", "Ne"]] },
  { k: "jidelnicek", nazev: "Využívá jídelníček", moznosti: [["ano", "Ano"], ["castecne", "Částečně"], ["ne", "Ne"]] },
  { k: "zmena", nazev: "Změna v jídle nebo energii", moznosti: [["ano", "Ano"], ["trochu", "Trochu"], ["zatim_ne", "Zatím ne"]] },
];

function ReportOkno({ r, mesic, onClose }) {
  const maxRoz = Math.max(1, ...[1, 2, 3, 4, 5].map((n) => Number(r.rozlozeni?.[n]) || 0));
  const pred = r.predchozi;
  return (
    <Modal open onClose={onClose} plnaHlavicka size="md" plne
      header={<HlavickaOkna ikona={BarChart3} nazev={`Report za ${nazevMesice(mesic).toLowerCase()}`} popis="Hodnocení nutričních konzultací" onClose={onClose} />}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Cislo popis="Konzultací" hodnota={r.konzultaci} />
          <Cislo popis="Obvoláno" hodnota={r.obvolano} />
          <Cislo popis="Dovoláno" hodnota={`${r.dovolano} (${r.podil_dovolanych} %)`} />
          <Cislo popis="Nedovoláno" hodnota={r.nedovolano} ton={r.nedovolano ? "pozor" : ""} />
          <Cislo popis="Nechtěly hodnotit" hodnota={r.nechce} />
          <Cislo popis="Žádosti o Google" hodnota={r.google} />
        </div>

        <div className="space-y-2.5 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-bold text-slate-900">Průměrné hodnocení</span>
            <Pil ton="znacka" casti={`${cz(r.prumer)} z 5 ★`} />
            {pred && pred.prumer != null && <Pil casti={`${mesicKratce(pred.mesic)} ${cz(pred.prumer)} ★`} />}
            {pred && pred.podil_dovolanych != null && <Pil casti={`dovoláno minule ${pred.podil_dovolanych} %`} />}
          </div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((n) => {
              const c = Number(r.rozlozeni?.[n]) || 0;
              return (
                <div key={n} className="flex items-center gap-2 text-[13px]">
                  <span className="w-9 shrink-0 font-bold tabular-nums text-slate-700">{n} ★</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span className={`block h-full rounded-full ${n >= 4 ? "bg-emerald-500" : "bg-rose-400"}`} style={{ width: `${(c / maxRoz) * 100}%` }} />
                  </span>
                  <span className="w-6 shrink-0 text-right font-semibold tabular-nums text-slate-600">{c}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
          {ROZDELENI.map((o) => (
            <div key={o.k} className="space-y-1.5 px-3 py-2.5 sm:px-4">
              <div className="text-[13px] font-semibold text-slate-500">{o.nazev}</div>
              <div className="flex flex-wrap gap-1.5">
                {o.moznosti.map(([k, l]) => <Pil key={k} casti={[l, String(Number(r[o.k]?.[k]) || 0)]} />)}
              </div>
            </div>
          ))}
        </div>

        {(r.citace || []).length > 0 && (
          <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
            <div className="text-[15px] font-bold text-slate-900">Shrnutí odpovědí</div>
            <ul className="space-y-2">
              {r.citace.map((c, i) => (
                <li key={i} className="text-[14px] leading-snug text-slate-700 [overflow-wrap:anywhere]">
                  <span className="font-bold text-slate-900">{c.nazev}:</span> „{c.text}“
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
          <div className="text-[15px] font-bold text-slate-900">Nespokojené klientky (1 až 3 hvězdy)</div>
          {(r.nespokojene || []).length === 0 ? (
            <div className="text-sm text-slate-500">Žádná, všechny dovolané byly spokojené.</div>
          ) : r.nespokojene.map((k) => (
            <div key={k.klient_id} className="space-y-1 rounded-xl border border-rose-100 bg-rose-50/50 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-slate-900">{k.klient_name}</span>
                <HvezdyPil n={k.hvezdy} />
              </div>
              <div className="text-[14px] leading-snug text-slate-700 [overflow-wrap:anywhere]">{k.vytka}</div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

/* --- Obrazovka hovoru (6B) ------------------------------------------------ */

function Otazka({ cislo, nazev, children }) {
  return (
    <div className="space-y-2.5 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
      <div className="flex items-start gap-2">
        {cislo != null && (
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[12px] font-bold text-brand-deep">{cislo}</span>
        )}
        <span className="text-[15px] font-bold leading-snug text-slate-900">{nazev}</span>
      </div>
      {children}
    </div>
  );
}

function HvezdyVyber({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} z 5`} aria-pressed={value === n}
          className="flex h-12 w-12 items-center justify-center rounded-xl transition active:scale-95 sm:h-11 sm:w-11 sm:hover:bg-amber-50">
          <Star className={`h-8 w-8 ${value >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
      {value ? <span className="ml-2 text-lg font-bold tabular-nums text-slate-800">{value}</span> : null}
    </div>
  );
}

/** Výsledek hovoru: velká tlačítka 2 × 2 (na mobilu se čtyři položky do jednoho řádku nevejdou). */
function VysledekVyber({ items, value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Výsledek hovoru">
      {items.map((it) => {
        const a = it.key === value;
        return (
          <button key={it.key} type="button" role="radio" aria-checked={a} onClick={() => onChange(it.key)}
            className={`min-h-[48px] rounded-xl px-3 py-2 text-[14px] font-bold leading-tight transition active:scale-[0.98] ${a ? "border-2 border-brand bg-brand-50 text-brand-deep" : "border border-slate-200 bg-white text-slate-700 sm:hover:bg-slate-50"}`}>
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/** Textová poznámka k výběru (otázky 3, 4b, 5, 6). */
function Poznamka({ value, onChange, dlouha }) {
  return (
    <textarea className={`field ${dlouha ? "min-h-[72px]" : "min-h-[44px]"}`} rows={dlouha ? 3 : 1} value={value || ""} maxLength={2000}
      onChange={(e) => onChange(e.target.value)} placeholder={dlouha ? "Co přesně říkala…" : "Poznámka (nepovinné)"} />
  );
}

function DrivejsiHodnoceni({ seznam }) {
  const [otevreno, setOtevreno] = useState(false);
  const obsahRef = useRef(null);
  // Po rozbalení plynule doskrolovat k obsahu (pravidlo adminu pro rozbalovací sekce).
  useEffect(() => {
    if (otevreno) obsahRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [otevreno]);
  if (!seznam || !seznam.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <button type="button" onClick={() => setOtevreno((o) => !o)} aria-expanded={otevreno}
        className="flex min-h-[48px] w-full items-center gap-2 px-3 text-left sm:px-4">
        <History className="h-4 w-4 shrink-0 text-brand-deep" />
        <span className="flex-1 text-[14px] font-bold text-slate-900">Dřívější hodnocení</span>
        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-brand-deep">{seznam.length}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
      </button>
      {otevreno && (
        <div ref={obsahRef} className="scroll-mt-4 space-y-2 border-t border-slate-100 p-3 sm:p-4">
          {seznam.map((h) => {
            const o = h.odpovedi || {};
            return (
              <div key={h.id} className="space-y-1.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[14px] font-bold text-slate-900">Konzultace {datumKratce(h.konzultace_datum, true)}</span>
                  {h.stav === "hotovo" ? <HvezdyPil n={h.hvezdy} /> : <Pil mala casti={STAV_POPIS[h.stav] || h.stav} />}
                  {h.nutricni && <Pil mala ton="znacka" casti={krestni(h.nutricni)} />}
                </div>
                {h.stav === "hotovo" && (
                  <div className="space-y-0.5 text-[13px] leading-snug text-slate-700 [overflow-wrap:anywhere]">
                    {OTAZKY_VYZIVA.filter((q) => q.typ !== "hvezdy" && o[q.k]).map((q) => (
                      <div key={q.k}><span className="font-semibold text-slate-500">{q.kratce || q.q}</span> {popisOdpovedi(q, o[q.k])}{q.pozn && o[q.pozn] ? `, ${o[q.pozn]}` : ""}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HovorOkno({ p, mesic, zbyvaDalsi, onClose, onUlozeno, onObnov }) {
  const z = p.zaznam;
  const zavrenoDobre = p.stav === "hotovo" || p.stav === "nechce";
  const pocatecni = p.stav === "nechce" ? "nechce" : p.stav === "nedovolano" ? "nedovolano_uzavreno" : "dovolano";
  const [vysledek, setVysledek] = useState(pocatecni);
  const [hvezdy, setHvezdy] = useState(z?.hvezdy || 0);
  const [odp, setOdp] = useState(() => ({ ...(z?.odpovedi || {}) }));
  const [poznamka, setPoznamka] = useState(z?.poznamka || "");
  const [poznamkaZnovu, setPoznamkaZnovu] = useState(z?.poznamka_znovu || "");
  const [nevolat, setNevolat] = useState(!!z?.nevolat);
  const [dalsi, setDalsi] = useState(!!z?.poznamka);
  const [uklada, setUklada] = useState(false);
  const [nahled, setNahled] = useState(null);
  const [posilaGoogle, setPosilaGoogle] = useState(false);
  const [googleKdy, setGoogleKdy] = useState(z?.google_odkaz_at || null);
  const [drivejsi, setDrivejsi] = useState(null);
  const ja = domaciJmeno(getCurrentUser()?.name) || "";
  const tel = String(p.telefon || "").replace(/\s/g, "");
  const nastav = (k, v) => setOdp((o) => ({ ...o, [k]: v }));
  // Nevolat příště patří jen k „Nechce hodnotit“, při změně výsledku se ruší.
  const zmenVysledek = (v) => { setVysledek(v); if (v !== "nechce") setNevolat(false); };
  const [OH, O2, O3, O4a, O4b, O5, O6, O7] = OTAZKY_VYZIVA;

  useEffect(() => {
    let zije = true;
    hvGet("klientka", { klientId: p.klient_id })
      .then((j) => { if (zije) setDrivejsi((j.hodnoceni || []).filter((h) => h.mesic !== mesic)); })
      .catch(() => { if (zije) setDrivejsi([]); });
    return () => { zije = false; };
  }, [p.klient_id, mesic]);

  // Druhý pokus po Nedovoláno už jen uzavírá, uložené hodnocení nejde změnit na nedovoláno.
  const vysledky = useMemo(() => {
    if (zavrenoDobre) return [{ key: "dovolano", label: "Dovoláno" }, { key: "nechce", label: "Nechce hodnotit" }];
    const prvni = p.stav === "zavolat";
    return [
      { key: "dovolano", label: "Dovoláno" },
      ...(prvni ? [{ key: "nedovolano", label: "Nedovoláno, 1. pokus" }] : []),
      { key: "nedovolano_uzavreno", label: "Nedovoláno, uzavřeno" },
      { key: "nechce", label: "Nechce hodnotit" },
    ];
  }, [zavrenoDobre, p.stav]);

  const telo = () => ({
    klientId: p.klient_id, mesic, vysledek,
    hvezdy: vysledek === "dovolano" ? hvezdy : null,
    odpovedi: vysledek === "dovolano" ? odp : {},
    poznamka, poznamka_znovu: poznamkaZnovu, nevolat: vysledek === "nechce" && nevolat,
  });

  const uloz = async () => {
    if (vysledek === "dovolano" && !hvezdy) { toast("Vyber hodnocení konzultace (1 až 5 hvězd).", "error"); return; }
    setUklada(true);
    try {
      const r = await hvUloz(telo());
      if (r.upozorneni && r.upozorneni.ok === false) toast(r.upozorneni.chyba || "Upozornění Kristině neodešlo, napiš jí prosím.", "error");
      else toast(r.zaznam?.stav === "zkusit_znovu" ? "Uloženo, klientka zůstává na řadě." : "Uloženo.", "success");
      if (r.reportVytvoren) toast("Všechny klientky jsou uzavřené, report měsíce je hotový.", "success");
      await onUlozeno(p);
    } catch (e) {
      toast(e.message, "error");
    } finally { setUklada(false); }
  };

  // Google: nejdřív se uloží hodnocení, pak náhled e-mailu, odeslání až tlačítkem v náhledu.
  const otevriNahled = async () => {
    if (posilaGoogle || uklada || vysledek !== "dovolano" || hvezdy < 4) return;
    setPosilaGoogle(true);
    try {
      await hvUloz(telo());
      onObnov?.();
      const j = await hvPost("google_nahled", { klientId: p.klient_id, mesic });
      setNahled(j.nahled);
    } catch (e) {
      toast(e.message, "error");
    } finally { setPosilaGoogle(false); }
  };
  const odeslatGoogle = async () => {
    if (posilaGoogle) return;
    setPosilaGoogle(true);
    try {
      const r = await hvGoogle({ klientId: p.klient_id, mesic });
      setGoogleKdy(new Date().toISOString());
      setNahled(null);
      toast(r.zapsanoDoKontaktu ? "E-mail odeslán, zapsáno i do kontaktu." : "E-mail s odkazem na Google odeslán.", "success");
      onObnov?.();
    } catch (e) {
      toast(e.message, "error");
    } finally { setPosilaGoogle(false); }
  };

  if (nahled) {
    return (
      <Modal open onClose={() => setNahled(null)} plnaHlavicka size="md" plne
        header={<HlavickaOkna ikona={Mail} nazev="Email s odkazem na hodnocení" popis={`${nahled.klient_name}, hodnocení ${nahled.hvezdy} ${hvezdTvar(nahled.hvezdy)}`} onClose={() => setNahled(null)} />}
        footer={(
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => setNahled(null)} className="btn-secondary justify-center">Zrušit</button>
            <button type="button" onClick={odeslatGoogle} disabled={posilaGoogle} className="btn-primary justify-center">
              <Send className="h-4 w-4" />{posilaGoogle ? "Odesílám…" : "Odeslat klientce"}
            </button>
          </div>
        )}>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex max-w-full items-center rounded-full bg-brand-50 px-2.5 py-1 text-[13px] font-bold text-brand-deep [overflow-wrap:anywhere]">Komu: {nahled.komu}</span>
            <span className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2.5 py-1 text-[13px] font-bold text-slate-700">Od: {nahled.od}</span>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-500">Předmět</div>
            <div className="field font-semibold">{nahled.subject}</div>
          </div>
          {/* HTML skládá server z pevného schváleného textu, jméno klientky je escapované. */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-4 [overflow-wrap:anywhere]" dangerouslySetInnerHTML={{ __html: nahled.html }} />
        </div>
      </Modal>
    );
  }

  const hlavicka = (
    <div className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-5">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 sm:h-10 sm:w-10">
          <Phone className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-serif text-lg font-bold leading-tight">{p.klient_name}</div>
          <div className="truncate text-xs text-white/70">Nutriční konzultace {datumKratce(p.datum, true)}</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.nutricni && <span className="rounded-lg bg-white/15 px-2 py-0.5 text-xs font-semibold">{p.nutricni}</span>}
            {p.minule && <span className="rounded-lg bg-white/15 px-2 py-0.5 text-xs font-semibold">minule {p.minule.hvezdy} ★ ({mesicKratce(p.minule.mesic)})</span>}
            {p.stav === "zkusit_znovu" && z?.pokusy > 0 && <span className="rounded-lg bg-amber-500/30 px-2 py-0.5 text-xs font-semibold">pokus {z.pokusy + 1}</span>}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Zavřít" title="Zavřít"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-white/20">
          <X className="h-5 w-5" />
        </button>
      </div>
      {tel ? (
        <a href={`tel:${tel}`} className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-white px-4 text-[15px] font-bold text-brand-deep shadow-sm transition active:scale-[0.98] sm:w-auto">
          <Phone className="h-5 w-5" />Zavolat {telefonHezky(tel)}
        </a>
      ) : (
        <div className="mt-3 rounded-xl bg-white/10 px-3 py-2 text-[13px]">Klientka nemá v kartě telefon.</div>
      )}
    </div>
  );

  const paticka = (
    <div className="flex items-center justify-end gap-2">
      <button type="button" onClick={onClose} className="btn-secondary hidden sm:inline-flex">Zavřít</button>
      <button type="button" onClick={uloz} disabled={uklada || posilaGoogle} className="btn-primary w-full justify-center sm:w-auto">
        {uklada ? "Ukládám…" : (p.stav !== "zavolat" && p.stav !== "zkusit_znovu") || !zbyvaDalsi ? "Uložit" : "Uložit a další klientka"}
        {!uklada && <ChevronRight className="h-4 w-4" />}
      </button>
    </div>
  );

  const nedovolano = vysledek === "nedovolano" || vysledek === "nedovolano_uzavreno";

  return (
    <Modal open onClose={onClose} header={hlavicka} plnaHlavicka footer={paticka} size="md" plne>
      <div className="space-y-3">
        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3 sm:p-4">
          <div className="flex items-center gap-2 text-[13px] font-bold text-brand-deep"><Mic className="h-4 w-4" />Co říct na začátku</div>
          <p className="mt-1.5 text-[15px] leading-relaxed text-brand-950">
            „Dobrý den, tady {ja || "…"} z FitWay40. Volám, protože jste měla {datumKratce(p.datum)} nutriční konzultaci{p.nutricni ? ` s ${sKym(p.nutricni)}` : ""}, a ráda bych se zeptala, jak jste byla spokojená. Zabere to asi 3 minuty, hodí se vám to teď?“
          </p>
          <p className="mt-2 text-[13px] leading-snug text-brand-900">
            Když se nehodí: <em>„Kdy vám mohu zavolat?“</em> Domluvený čas zapiš do poznámky u Nedovoláno, 1. pokus.
          </p>
        </div>

        <OKlientceRozbalovaci klientId={p.klient_id} />

        <DrivejsiHodnoceni seznam={drivejsi} />

        <Otazka nazev="Jak hovor dopadl?">
          <VysledekVyber items={vysledky} value={vysledek} onChange={zmenVysledek} />
        </Otazka>

        {nedovolano && (
          <Otazka nazev={vysledek === "nedovolano" ? "Poznámka k dalšímu pokusu" : "Poznámka (nepovinné)"}>
            <input className="field" value={poznamkaZnovu} maxLength={300} onChange={(e) => setPoznamkaZnovu(e.target.value)}
              placeholder={vysledek === "nedovolano" ? "domluvený čas, např. zítra v 17:00" : "např. nebere telefon"} />
            <p className="text-[13px] text-slate-500">
              {vysledek === "nedovolano"
                ? "Klientka zůstane na řadě jako Zkusit znovu, poznámka se ukáže u jejího jména."
                : "Klientka se už volat nebude a v reportu bude jako nedovolaná."}
            </p>
          </Otazka>
        )}

        {vysledek === "nechce" && (
          <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
            <Zapinatko zapnuto={nevolat} onZmena={setNevolat} popisek="Nevolat příště" ikona={PhoneOff} />
            <p className="text-[13px] text-slate-500">Kontakt dostane označení Nevolat a do dalších seznamů k obvolání se nedostane.</p>
          </div>
        )}

        {vysledek === "dovolano" && (
          <>
            <Otazka cislo={1} nazev={OH.q}>
              <HvezdyVyber value={hvezdy} onChange={setHvezdy} />
            </Otazka>
            <Otazka cislo={2} nazev={O2.q}>
              <textarea className="field min-h-[72px]" value={odp.pomohlo || ""} maxLength={2000} onChange={(e) => nastav("pomohlo", e.target.value)} placeholder="Napiš odpověď…" />
            </Otazka>
            <Otazka cislo={3} nazev={O3.q}>
              <Segment items={O3.moznosti} value={odp.informace} onChange={(v) => nastav("informace", v)} aria-label="Bylo informací akorát" />
              <Poznamka value={odp.informace_pozn} onChange={(v) => nastav("informace_pozn", v)} dlouha />
            </Otazka>
            <Otazka cislo={4} nazev="Masterplan">
              <div className="text-[13px] font-semibold text-slate-600">{O4a.kratce}</div>
              <Segment items={O4a.moznosti} value={odp.mp_zajem} onChange={(v) => nastav("mp_zajem", v)} aria-label="Zájem o masterplan" />
              {odp.mp_zajem === "ano" && (
                <>
                  <div className="text-[13px] font-semibold text-slate-600">{O4b.kratce}</div>
                  <Segment items={O4b.moznosti} value={odp.mp_dodrzuje} onChange={(v) => nastav("mp_dodrzuje", v)} aria-label="Dodržování masterplanu" />
                  <Poznamka value={odp.mp_dodrzuje_pozn} onChange={(v) => nastav("mp_dodrzuje_pozn", v)} />
                </>
              )}
            </Otazka>
            <Otazka cislo={5} nazev={O5.q}>
              <Segment items={O5.moznosti} value={odp.jidelnicek} onChange={(v) => nastav("jidelnicek", v)} aria-label="Využívá jídelníček" />
              <Poznamka value={odp.jidelnicek_pozn} onChange={(v) => nastav("jidelnicek_pozn", v)} />
            </Otazka>
            <Otazka cislo={6} nazev={O6.q}>
              <Segment items={O6.moznosti} value={odp.zmena} onChange={(v) => nastav("zmena", v)} aria-label="Změna v jídle nebo energii" />
              <Poznamka value={odp.zmena_pozn} onChange={(v) => nastav("zmena_pozn", v)} dlouha />
            </Otazka>
            <Otazka cislo={7} nazev={O7.q}>
              <textarea className="field min-h-[72px]" value={odp.priste || ""} maxLength={2000} onChange={(e) => nastav("priste", e.target.value)} placeholder="Napiš odpověď…" />
            </Otazka>

            {hvezdy >= 4 && (
              <div className={`space-y-2.5 rounded-2xl border-2 p-3 sm:p-4 ${googleKdy || !p.google_kdy ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                <div className={`flex items-center gap-2 text-[15px] font-bold ${googleKdy || !p.google_kdy ? "text-emerald-900" : "text-slate-700"}`}>
                  <Star className="h-5 w-5 fill-amber-400 text-amber-400" />Klientka je spokojená
                </div>
                {googleKdy ? (
                  <Stitek ton="ok" ikona={CheckCircle2}>Odkaz na Google odeslán {datumZCasuSRokem(googleKdy)}</Stitek>
                ) : p.google_kdy ? (
                  <>
                    <p className="text-[14px] leading-snug text-slate-600">Žádost o Google hodnocení už klientka dostala dřív, podruhé ji neposíláme.</p>
                    <button type="button" disabled
                      className="btn inline-flex min-h-[48px] w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-slate-200 px-4 font-bold text-slate-500 sm:w-auto">
                      <Lock className="h-4 w-4" />Odesláno {datumZCasuSRokem(p.google_kdy)}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-[14px] leading-snug text-emerald-900">
                      Zeptej se: <em>„Mohla bych vám poslat odkaz, kdybyste nám chtěla napsat pár slov na Google? Moc by nám to pomohlo.“</em> Když řekne ano, pošli e-mail. Před odesláním uvidíš náhled.
                    </p>
                    <button type="button" disabled={uklada || posilaGoogle} onClick={otevriNahled}
                      className="btn inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 font-bold text-white hover:bg-emerald-700 disabled:opacity-60 sm:w-auto">
                      <Send className="h-4 w-4" />{posilaGoogle ? "Připravuji…" : "Poslat email s odkazem na hodnocení"}
                    </button>
                  </>
                )}
              </div>
            )}
            {hvezdy > 0 && hvezdy <= 3 && (
              <Hlaska ton="pozor" title="Klientka není úplně spokojená">
                Po uložení dostane Kristina upozornění s odpověďmi na Slacku a ve zvonečku, ať se jí může ozvat.
              </Hlaska>
            )}
          </>
        )}

        <button type="button" onClick={() => setDalsi((o) => !o)} aria-expanded={dalsi}
          className="btn-ghost w-full justify-between">
          <span className="inline-flex items-center gap-2"><NotebookPen className="h-4 w-4" />Další možnosti</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${dalsi ? "" : "-rotate-90"}`} />
        </button>
        {dalsi && (
          <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
            <label className="block space-y-1.5">
              <span className="text-[13px] font-bold text-slate-700">Interní poznámka</span>
              <textarea className="field min-h-[64px]" value={poznamka} maxLength={2000} onChange={(e) => setPoznamka(e.target.value)} placeholder="Vidí jen tým, klientka ne" />
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
